/**
 * What is left of the row helpers once the API answers for itself.
 *
 * This file used to reconstruct, per row, what the backend now states
 * outright — which candidate a row points at (walking `chosenRank` over
 * `alternatives`), what its percentage was (falling back through similarity
 * and distance), whether it was an AI proposal (sniffing metadata), what a
 * metadata bag held. All of that is now a typed field on `row.target` /
 * `row.source`, resolved server-side by the one function every backend read
 * path shares.
 *
 * What survives is the one thing the client still decides: how a pending,
 * unsaved edit changes what the row shows before the server has confirmed it.
 */

import { ReviewStatus, type RowEdit } from "@models/job";
import type {
  MappingResultRow,
  MappingResultTarget,
} from "@models/mappingResult";

/** Placeholder rendered when a cell has no value. */
export const PLACEHOLDER = "-";

/** Maps a review status to the colour of the small status dot. */
export const STATUS_DOT_COLOR: Readonly<Record<ReviewStatus, string>> = {
  [ReviewStatus.Pending]: "gray",
  [ReviewStatus.Accepted]: "teal",
  [ReviewStatus.Rejected]: "red",
  [ReviewStatus.Corrected]: "blue",
  [ReviewStatus.Failed]: "red",
};

/**
 * The target a row displays, honouring an unsaved edit.
 *
 * `row.target` is the server's answer. The rejected state overrides it
 * client-side so the target cell is blank.
 */
export const resolveDisplayTarget = <T extends MappingResultTarget>(
  row: MappingResultRow & { target: T | null },
  edit: RowEdit | undefined,
): T | null => {
  if ((edit?.reviewStatus ?? row.reviewStatus) === ReviewStatus.Rejected) {
    return null;
  }
  return row.target;
};
